/**
 * CWB structured conditional — shared serialization helper.
 *
 * Canonical (backend) conditional shape:
 *   {
 *     "op": "AND" | "OR",
 *     "clauses": [
 *       { "parentCanonicalId": "CQ-0007", "operator": "==", "value": "Yes" }
 *     ]
 *   }
 *
 * `parentCanonicalId` is the PARENT canonical question's entity_id (NOT its
 * machine_key). Operators needing multiple operands carry them alongside
 * `operator`:
 *   - normal operators -> `value`
 *   - "includes"       -> `values: string[]`   (BE array representation — see
 *                          note below; isolated here until BE-confirmed)
 *   - "in-range"       -> `lower`, `upper`      (documented naming)
 *
 * All (de)serialization goes through this module so the builder UI, the
 * placement editor, the store, and the mock stay in exact agreement.
 *
 * CONTRACT NOTE (includes): the exact wire representation of `includes` is not
 * yet BE-confirmed. It is kept here as `values: string[]`. If BE requires a
 * different shape, change ONLY `normalizeClause`/`serializeConditional`.
 */
import { operatorValueArity } from "@/config/cwbWidgetTypes";

export const CONDITIONAL_OPS = ["AND", "OR"];

export function makeEmptyClause() {
  return { parentCanonicalId: null, operator: "==", value: "" };
}

export function makeEmptyConditional() {
  return { op: "AND", clauses: [makeEmptyClause()] };
}

/** True when a conditional has no usable clauses. */
export function isConditionalEmpty(conditional) {
  return !conditional || !Array.isArray(conditional.clauses) || conditional.clauses.length === 0;
}

/** Keep only the operand fields relevant to a clause's operator. */
export function normalizeClause(clause) {
  const base = {
    parentCanonicalId: clause.parentCanonicalId || null,
    operator: clause.operator || "==",
  };
  const arity = operatorValueArity(base.operator);
  if (arity === "list") {
    // `includes` — BE array representation (isolated; see CONTRACT NOTE).
    base.values = Array.isArray(clause.values) ? clause.values.filter((v) => v !== "" && v != null) : [];
  } else if (arity === "range") {
    // `in-range` — documented `lower`/`upper` naming (accept legacy min/max in).
    const lower = clause.lower ?? clause.min;
    const upper = clause.upper ?? clause.max;
    base.lower = lower === "" || lower === undefined || lower === null ? null : Number(lower);
    base.upper = upper === "" || upper === undefined || upper === null ? null : Number(upper);
  } else {
    base.value = clause.value ?? "";
  }
  return base;
}

/**
 * Normalize any inbound conditional (legacy `logic`/`parent_canonical_key`/
 * `min`/`max` included) into the canonical shape. Returns null when empty.
 */
export function normalizeConditional(conditional) {
  if (!conditional) return null;
  const clausesIn = Array.isArray(conditional.clauses) ? conditional.clauses : [];
  if (!clausesIn.length) return null;

  const op = (conditional.op || conditional.logic) === "OR" ? "OR" : "AND";
  const clauses = clausesIn.map((c) =>
    normalizeClause({
      // accept both new and legacy parent field names
      parentCanonicalId:
        c.parentCanonicalId ??
        c.parent_canonical_id ??
        c.parent_canonical_key ??
        c.parent_key ??
        null,
      operator: c.operator,
      value: c.value,
      values: c.values,
      lower: c.lower,
      upper: c.upper,
      min: c.min,
      max: c.max,
    })
  );
  return { op, clauses };
}

/**
 * Serialize the builder's working model to the canonical shape (or null).
 *
 * `controlFor(clause)` (optional) returns the resolved value control for a
 * clause (see conditionalValueControl). When it reports "number", a non-empty
 * single `value` is coerced with Number() — Vuetify number inputs emit strings
 * and numeric comparisons must persist as numbers. Text / option / yes-no
 * values stay strings; `in-range` lower/upper are already numeric.
 */
export function serializeConditional(op, clauses, controlFor = null) {
  const list = (clauses || []).map((clause) => {
    const norm = normalizeClause(clause);
    if (controlFor && operatorValueArity(norm.operator) === "single") {
      if (controlFor(clause) === "number" && norm.value !== "" && norm.value != null) {
        norm.value = Number(norm.value);
      }
    }
    return norm;
  });
  if (!list.length) return null;
  return { op: op === "OR" ? "OR" : "AND", clauses: list };
}

/**
 * Decide which value control a clause should render, given the operator and
 * the selected parent question's metadata.
 *
 * Returns one of: "range" | "options-multi" | "yesno" | "option" | "number" | "text".
 * parentMeta: { widgetType, answerType, answerOptions } (any may be missing)
 */
export function conditionalValueControl(operator, parentMeta = {}) {
  const arity = operatorValueArity(operator);
  if (arity === "range") return "range";
  if (arity === "list") return "options-multi";

  const widget = parentMeta.widgetType || "";
  const answerType = (parentMeta.answerType || "").toLowerCase();
  const hasOptions = Array.isArray(parentMeta.answerOptions) && parentMeta.answerOptions.length > 0;
  const isYesNo = widget === "yes_no" || answerType === "yes/no";
  const isNumeric =
    widget === "number" || widget === "percentage" || answerType === "number" || answerType === "percentage";
  const numericComparator = [">", ">=", "<", "<="].includes(operator);

  if (isYesNo) return "yesno";
  if (hasOptions) return "option";
  if (numericComparator || isNumeric) return "number";
  return "text";
}
